import React, { useContext, useEffect, useState } from 'react';
import { SupplierDirectoryType } from '@ordermentum/ui-types';
import { ThemeProvider } from 'emotion-theming';
import { BREAKPOINT_VALUES as B } from '@ordermentum/ui-constants';
import { MEDIA_QUERIES as M } from '../../../foundations/grid';
import { ColorCodes, colorsForTheme } from '../../../foundations/color';
import { genThemedStyled, Theme, ThemeContext } from '../../../utilities/theme';
import S from '../../../foundations/size';
import { FUTURO_TYPEFACES as F } from '../../../foundations/font';
import { Icon } from '../../../foundations';
import { generateQuote } from '../utilities';

type Props = {
  supplier: SupplierDirectoryType;
  showInformation?: boolean;
  signedIn?: boolean;
};

type themeStyles = {
  backgroundColor: ColorCodes;
  borderColor: ColorCodes;
  headingColor: ColorCodes;
  textColor: ColorCodes;
  iconBackgroundColor: ColorCodes;
  quoteColor: ColorCodes;
};

const getTheme = (theme: Theme): themeStyles => {
  const colors = colorsForTheme(theme);
  const colorThemes = {
    backgroundColor: colors.SMOKE,
    borderColor: colors.ASH,
    headingColor: colors.SPACE,
    textColor: colors.STORM,
    iconBackgroundColor: colors.SWAN,
    quoteColor: colors.SWEET_ORANGE,
  };
  return colorThemes;
};

const ThemeStyled = genThemedStyled<themeStyles>();

const Wrapper = ThemeStyled.div<{
  theme: themeStyles;
}>(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  flexWrap: 'wrap',
  backgroundColor: theme.backgroundColor,
  paddingRight: '7.188rem',

  [M.TABLET_LANDSCAPE_AND_SMALLER]: {
    padding: `0 ${S.XXL}`,
    justifyContent: 'flex-start',
  },
  [M.TABLET_PORTRAIT_AND_SMALLER]: {
    padding: `0 ${S.XL}`,
    backgroundColor: 'unset',
    justifyContent: 'flex-start',
  },
  [M.MOBILE_ONLY]: {
    padding: `0 ${S.S}`,
    paddingBottom: S.S,
    backgroundColor: 'unset',
    justifyContent: 'flex-start',
  },
}));

const Content = ThemeStyled.div({
  display: 'block',
  width: '100%',
  maxWidth: '30.5rem',
  [M.TABLET_LANDSCAPE_AND_SMALLER]: {
    maxWidth: '27.063rem',
  },
  [M.TABLET_PORTRAIT_AND_SMALLER]: {
    maxWidth: '100%',
  },
  [M.MOBILE_ONLY]: {
    maxWidth: 'fit-content',
  },
});

const BrandingContainer = ThemeStyled.div<{
  signedIn?: boolean;
}>(({ signedIn }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: S.M,
  marginTop: '4rem',
  [M.SMALL_DESKTOP_AND_SMALLER]: {
    marginTop: S.XL,
  },
  [M.TABLET_LANDSCAPE_AND_SMALLER]: {
    marginTop: S.XXXL,
  },
  [M.TABLET_PORTRAIT_AND_SMALLER]: {
    marginTop: signedIn ? S.XXXL : S.XL,
  },
  [M.MOBILE_ONLY]: {
    marginTop: S.XL,
  },
}));

const Logo = ThemeStyled.img(p => ({
  border: `0.063rem solid ${p.theme.borderColor}`,
  borderRadius: S.XXXS,
  height: S.XXXXL,
}));

const TradingName = ThemeStyled.h2(
  p => ({
    margin: 'auto 0',
    color: p.theme.headingColor,

    [M.TABLET_PORTRAIT_AND_SMALLER]: {
      ...F.H3,
    },
    [M.MOBILE_ONLY]: {
      ...F.H4,
    },
  }),
  F.H2
);

const BrandMedia = ThemeStyled.div({
  position: 'relative',
  width: '100%',
  margin: `${S.XL} 0`,
  [M.MOBILE_ONLY]: {
    minWidth: 0,
  },
});

const BrandImage = ThemeStyled.img<{
  show: boolean;
}>(({ show }) => ({
  display: show ? 'block' : 'none',
  borderRadius: S.XS,
  objectFit: 'cover',
  width: '30.5rem',
  [M.TABLET_PORTRAIT_AND_SMALLER]: {
    width: '100%',
  },
}));

const BrandVideo = ThemeStyled.video({
  display: 'block',
  zIndex: 1,
});

const Overlay = ThemeStyled.div<{
  show?: boolean;
}>(({ show }) => ({
  display: show ? 'flex' : 'none',
  position: 'absolute',
  background: 'rgba(0, 0, 0, 0.2)',
  alignItems: 'center',
  justifyContent: 'center',
  inset: 0,
  borderRadius: S.XS,
  width: '30.5rem',
  maxWidth: '100%',
  [M.TABLET_PORTRAIT_AND_SMALLER]: {
    width: '100%',
  },
}));

const PlayIcon = ThemeStyled.button(p => ({
  backgroundColor: p.theme.iconBackgroundColor,
  borderRadius: '50%',
  borderColor: 'transparent',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '4.125rem',
  height: '4.125rem',
}));

const BannerText = ThemeStyled.h4(
  p => ({
    color: p.theme.textColor,
  }),
  F.H4
);

const Quote = ThemeStyled.p(
  p => ({
    color: p.theme.textColor,
    whiteSpace: 'pre-wrap',
  }),
  F.LABEL
);

const Information = ThemeStyled.div<{
  display?: boolean;
}>(props => ({
  [M.TABLET_PORTRAIT_AND_SMALLER]: {
    display: props?.display ? 'block' : 'none',
  },
}));

const ReadMoreContainer = ThemeStyled.a(({ theme }) => ({
  color: theme.quoteColor,
  display: 'inline',
  ...F.BODY,
}));

const SupplierInformation = (props: Props) => {
  const { supplier, showInformation = false, signedIn = false } = props;
  const { supplierQuote = '' } = supplier.dataAttributes || {};
  const [isMobile, setIsMobile] = useState<boolean>(
    globalThis?.screen.width <= B.MOBILE_MAX
  );
  const theme = getTheme(useContext(ThemeContext));
  const [showVideo, setShowVideo] = useState<boolean>(false);
  const [showReadMore, setShowReadMore] = useState<boolean>(true);
  const reducedQuote = generateQuote(supplierQuote, 25);
  const [quote, setQuote] = useState<string>(
    isMobile ? reducedQuote : supplierQuote
  );

  useEffect(() => {
    const handleResize = e => {
      setIsMobile(e?.target?.screen.width <= B.MOBILE_MAX);
    };

    globalThis?.addEventListener('resize', handleResize);
    return () => globalThis?.removeEventListener('resize', handleResize);
  });

  useEffect(() => {
    if (isMobile) {
      setQuote(reducedQuote);
      setShowReadMore(true);
    } else {
      setQuote(supplierQuote);
    }
  }, [isMobile]);

  return (
    <ThemeProvider theme={theme}>
      <Wrapper>
        <Content>
          <BrandingContainer signedIn={signedIn}>
            {supplier.logo?.icon && (
              <Logo
                src={supplier.logo.icon}
                alt={`${supplier.tradingName} logo`}
              />
            )}
            <TradingName>{supplier.tradingName}</TradingName>
          </BrandingContainer>

          <Information display={showInformation}>
            <BrandMedia>
              {supplier.brandImages && supplier.brandImages.length > 0 && (
                <BrandImage
                  src={supplier.brandImages[0].original}
                  alt={`${supplier.tradingName} branding`}
                  show={!showVideo}
                />
              )}

              {supplier.dataAttributes?.videoUrl && (
                <Overlay show={!showVideo}>
                  <PlayIcon onClick={() => setShowVideo(true)}>
                    <Icon name="lPlayVideo" />
                  </PlayIcon>
                </Overlay>
              )}

              {supplier.dataAttributes?.videoUrl && showVideo && (
                <BrandVideo width="100%" height="275" controls autoPlay>
                  <source src={supplier.dataAttributes?.videoUrl} />
                </BrandVideo>
              )}
            </BrandMedia>

            <BannerText>
              Ordermentum makes it easy to order from {supplier.tradingName}
            </BannerText>

            <Quote>
              {quote}
              {isMobile && supplierQuote !== reducedQuote && (
                <ReadMoreContainer
                  onClick={() => {
                    setShowReadMore(!showReadMore);
                    setQuote(showReadMore ? supplierQuote : reducedQuote);
                  }}
                >
                  {showReadMore ? ' read more' : ' read less'}
                </ReadMoreContainer>
              )}
            </Quote>
          </Information>
        </Content>
      </Wrapper>
    </ThemeProvider>
  );
};

export default SupplierInformation;
